(ns io.daskul.webapp.widgets.dashboard.events
  "Dashboard re-frame events — fetch dashboard data from backend."
  (:require
   [ajax.core]
   [re-frame.core :as rf]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.widgets.dashboard.dates :as dates]))

;; ============================================================================
;; Configuration (product-specific injection)
;; ============================================================================

(defonce ^:private config
  (atom {:href-fn nil}))  ;; (fn [route-name path-params query-params]) -> url-string

(defn configure!
  "Configure the dashboard module with product-specific callbacks.

   Options:
   - :href-fn — (fn [route-name path-params query-params]) -> url-string
                Used to resolve metric href maps into proper URLs with
                school-code prefix and query params."
  [opts]
  (swap! config merge opts))

(defn- resolve-href
  "Resolve a metric href. Supports:
   - {:agg \"enrollment\" :query {...}} — aggregate list with query params
   - {:route \"billing/unbilled\"} — custom named route
   - string — returned as-is"
  [db href]
  (cond
    (string? href) href
    (not (map? href)) nil
    (:route href) (when-let [f (:href-fn @config)]
                    (f (keyword (:route href)) {} (:query href)))
    :else (when-let [f (:href-fn @config)]
            (let [agg-key (keyword (:agg href))
                  resource (get-in db [:config :agg-key->resource agg-key])]
              (f :aggregate/list {:agg resource} (:query href))))))

(defn- resolve-item-href
  "Resolve an item-level href. Supports {:agg \"lead\" :id \"uuid\"} for
   individual records (routes to :aggregate/detail)."
  [db href]
  (if (and (map? href) (:id href))
    (when-let [f (:href-fn @config)]
      (let [agg-key (keyword (:agg href))
            resource (get-in db [:config :agg-key->resource agg-key])]
        (f :aggregate/detail {:agg resource :id (:id href)} nil)))
    (resolve-href db href)))

(defn- resolve-metric-hrefs
  "Walk a metric and resolve any :href maps to URL strings.
   For :item-list widgets, also resolves hrefs on individual items
   and :view-all-href."
  [db metric]
  (cond-> metric
    (:href metric)
    (update :href #(resolve-href db %))

    (:items metric)
    (update :items (fn [items]
                     (mapv #(if (:href %)
                              (update % :href (partial resolve-item-href db))
                              %)
                           items)))

    (:view-all-href metric)
    (update :view-all-href #(resolve-href db %))))

(defn- resolve-section-hrefs
  "Resolve all metric hrefs in a section's metrics vector."
  [db section]
  (update section :metrics #(mapv (partial resolve-metric-hrefs db) %)))

;; ============================================================================
;; Date helpers
;; ============================================================================

(def ^:private format-date
  "See widgets.dashboard.dates — defined identically here and in date-range
   until #749 gave it one home."
  dates/format-date)

(def ^:private preset-dates
  "See widgets.dashboard.dates/preset-range — one home, so the applied range and
   the header's date label never drift."
  dates/preset-range)

;; ============================================================================
;; Events
;; ============================================================================

(rf/reg-event-fx
 ::set-date-range
 (fn [{:keys [db]} [_ preset]]
   (let [dates (preset-dates preset)]
     {:db       (assoc-in db [:dashboard :date-range]
                          {:preset preset
                           :start-date (:start-date dates)
                           :end-date   (:end-date dates)})
      :dispatch [::fetch dates]})))

(rf/reg-event-db
 ::set-custom-mode
 (fn [db _]
   (assoc-in db [:dashboard :date-range :preset] "custom")))

(rf/reg-event-fx
 ::set-custom-date-range
 (fn [{:keys [db]} [_ start-date end-date]]
   {:db       (assoc-in db [:dashboard :date-range]
                         {:preset     "custom"
                          :start-date start-date
                          :end-date   end-date})
    :dispatch [::fetch {:start-date start-date :end-date end-date}]}))

(rf/reg-event-fx
 ::fetch
 (fn [{:keys [db]} [_ params]]
   (let [query-string (when (and (:start-date params) (:end-date params))
                        (str "?start-date=" (:start-date params)
                             "&end-date=" (:end-date params)))]
     {:db (-> db
              (assoc-in [:dashboard :loading?] true)
              (assoc-in [:dashboard :error] nil))
      :http-xhrio {:method          :get
                    :uri             (str (agg.events/get-api-base) "/dashboard" query-string)
                    :headers         (agg.events/get-auth-header)
                    :response-format (ajax.core/json-response-format {:keywords? true})
                    :on-success      [::fetch-success]
                    :on-failure      [::fetch-failure]}})))

(rf/reg-event-db
 ::fetch-success
 (fn [db [_ response]]
   (-> db
       (assoc-in [:dashboard :loading?] false)
       (assoc-in [:dashboard :sections] (mapv (partial resolve-section-hrefs db) (:sections response)))
       (assoc-in [:dashboard :date-range-response] (:date-range response)))))

(rf/reg-event-db
 ::fetch-failure
 (fn [db [_ error]]
   (-> db
       (assoc-in [:dashboard :loading?] false)
       (assoc-in [:dashboard :error]
                 (or (get-in error [:response :message])
                     "Failed to load dashboard")))))
